import {IProviderRoutes} from "../interfaces/provider";
import Provider from "./new/provider";
import {Constructor} from "../../global/constructor";
import {IServerData} from "../interfaces/serverInterfaces";
import {HTTPMethod} from "./new/httpMethod";
import DetailView from "../models/detailView/detailView";
import {Route} from "../enums/route";
import {RouteHelper} from "../helpers/routeHelper";
import { IObjectModelEntry } from '~/interfaces/objectModelInterfaces';

interface IDetailViewProviderRoutes{
  create: string;
  getListView: string,
  getHistory: string,
  getContentElementLinkedForms: Route,
  getContentElementLinkedFormSubmissions: Route,
  publish: Route
  publishingDependencies: Route,
  clone: Route
  entryShow: string
}

export default class DetailViewProvider<T extends DetailView = DetailView> extends Provider<T> {
  routes: IProviderRoutes & IDetailViewProviderRoutes;

  constructor(protected modelType: Constructor<T>, public modelRoot: string, public collectionRoot: string) {
    super(modelType, modelRoot, collectionRoot);
    let newRoutes: IDetailViewProviderRoutes = {
      create: RouteHelper.path({route: Route.detailViews}),
      getListView: RouteHelper.path({route: Route.associatedListViewDetailView}),
      getContentElementLinkedForms: Route.detailViewContentElementLinkedForms,
      getContentElementLinkedFormSubmissions: Route.detailViewContentElementLinkedFormSubmissions,
      getHistory: RouteHelper.path({route: Route.modelHistoryView}),
      publish: Route.publishDetailView,
      publishingDependencies: Route.detailViewPublishingDependencies,
      clone: Route.detailViewClone,
      entryShow: RouteHelper.path({ route: Route.detailViewEntryShow }),
    };
    Object.assign(this.routes, newRoutes);
  }

  replacer(route: string, obj: object, metadata: object = {}): string {
    return route.replace(':detail_view_id', obj['detail_view_id']).replace(':list_view_id', obj['list_view_id']).replace(':entry_id', obj['entry_id']).replace(':history_id', obj['history_id']);
  }

  create({organizationId, folderIdTree, detailView}: {organizationId: string, folderIdTree: string, detailView: object}): Promise<IServerData<any>> {
    const json = {
      organization_id: organizationId,
      folder_id_tree: folderIdTree,
      detail_view: detailView,
    };
    return new Promise<IServerData>((resolve, reject) => {
      this.serverAction({
        endpoint: this.routes.create,
        method: HTTPMethod.POST,
        json: json,
      }).then(resolve);
    });
  }

  entryShow({detailViewId, entryId}: {detailViewId: string, entryId: string}): Promise<IServerData<{ entry: IObjectModelEntry }>> {
    return this.serverAction({
      endpoint: this.replacer(this.routes.entryShow, {
        detail_view_id: detailViewId,
        entry_id: entryId,
      }),
      method: HTTPMethod.GET,
    });
  }

  fetchAssociatedListView({organizationId, detailViewId, listViewId, entryId, relatedObjectElementId}: {organizationId: string, detailViewId: string, listViewId: string, entryId: string, relatedObjectElementId?: string}): Promise<IServerData<any>> {
    const json = {
      organization_id: organizationId,
      detail_view_id: detailViewId,
      list_view_id: listViewId,
      entry_id: entryId,
    };
    return new Promise<IServerData>((resolve, reject) => {
      this.serverAction({
        endpoint: this.replacer(this.routes.getListView, json),
        method: HTTPMethod.GET,
        json: json,
      }).then(resolve);
    });
  }

  fetchHistory({organizationId, detailViewId, entryId, modelHistoryId, fetchIdx, initialFetch, relatedObjectElementId}: {organizationId: string, detailViewId: string, entryId: string, modelHistoryId: string, fetchIdx: number, initialFetch: boolean, relatedObjectElementId?: string}): Promise<IServerData<any>> {
    const json = {
      organization_id: organizationId,
      detail_view_id: detailViewId,
      entry_id: entryId,
      history_id: modelHistoryId,
      fetch_idx: fetchIdx,
    };
    if (initialFetch) json["initial_fetch"] = true;
    return new Promise<IServerData>((resolve, reject) => {
      this.serverAction({
        endpoint: this.replacer(this.routes.getHistory, json),
        method: HTTPMethod.GET,
        json: json,
      }).then(resolve);
    });
  }

  async fetchLinkedFormSubmissions({organizationId, detailViewId, contentElementId, recordId, linkedFormIds, filters, pagination}) {
    const json = {
      organization_id: organizationId,
      detail_view_id: detailViewId,
      content_element_id: contentElementId,
      record_id: recordId,
      linked_form_ids: linkedFormIds,
      filters: filters,
      pagination: pagination
    };
    return new Promise<any>((resolve, reject) => {
      this.serverAction<any>({
        endpoint: RouteHelper.path({route: this.routes.getContentElementLinkedFormSubmissions, modelOrId: detailViewId}),
        method: HTTPMethod.GET,
        json: json,
      }).then(([res]) => {
        if (res.success) {
          resolve({
            submissionJson: res.submissions,
            querySubmissionCount: res.query_submission_count,
            totalSubmissionCount: res.total_submission_count,
            pagination: res.pagination
          });
        } else {
          console.error(`Attempt to fetch linked form submissions for detail view: ${detailViewId} content element: ${contentElementId} failed.`);
        }

      }).catch(reject);
    });
  }

  fetchLinkedForms({organizationId, detailViewId, contentElementId, recordId}) {
    const json = {
      organization_id: organizationId,
      detail_view_id: detailViewId,
      content_element_id: contentElementId,
      record_id: recordId
    };
    return new Promise<IServerData>((resolve, reject) => {
      this.serverAction<any>({
        endpoint: RouteHelper.path({route: this.routes.getContentElementLinkedForms, modelOrId: detailViewId}),
        method: HTTPMethod.GET,
        json: json,
      }).then(([res]) => {
        if (res.success) {
          resolve(res.linked_forms);
        } else {
          console.error(`Attempt to fetch linked forms for detail view: ${detailViewId} content element: ${contentElementId} failed.`);
        }

      }).catch(reject);
    });
  }

  publish(detailView: DetailView): Promise<IServerData> {
    return new Promise<IServerData>((resolve, reject) => {
      this.serverAction({
        endpoint: RouteHelper.path({route: this.routes.publish, modelOrId: detailView}),
        method: HTTPMethod.POST,
      }).then(resolve)
    })
  }

  clone({organizationId, folderIdTree, detailViewId, title, reference_name}) {
    let json =  {
      title: title,
      reference_name: reference_name,
      detail_view_id: detailViewId,
      organization_id: organizationId,
      folder_id_tree: folderIdTree
    };
    return new Promise<IServerData>((resolve, reject) => {
      this.serverAction({
        endpoint:  RouteHelper.path({route: this.routes.clone, modelOrId: detailViewId}),
        method: HTTPMethod.POST,
        json: json
      }).then(resolve)
    })
  }

  publishingDependencies(obj: T): Promise<IServerData> {
    return new Promise<IServerData>((resolve, reject) => {
      this.serverAction({
        endpoint: RouteHelper.path({route: this.routes.publishingDependencies, modelOrId: obj}),
        method: HTTPMethod.GET,
      }).then(resolve)
    })
  }
}
